import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import styled from 'styled-components'
import Logout from '@/components/icons/Logout'
import QuestionMarkIcon from '@/components/icons/QuestionMarkIcon'
import HomeIcon from '@/components/icons/HomeIcon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExpand, faCompress } from '@fortawesome/free-solid-svg-icons'
import fscreen from 'fscreen' // eslint-disable-line @typescript-eslint/no-unsafe-assignment
import { isApp, openLink } from '@/helpers'
import { withScreenInfo } from '../withScreenInfo'

const Container = styled.div`
  max-width: 100%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  color: white;
  flex-grow: 0;
  min-height: 58px;
  height: 58px;
`

const Side = styled.div<{ isMobile?: boolean }>`
  width: 140px;
  width: ${({ isMobile }) => isMobile ? '70px' : '140px'};
  display: flex;
  align-items: center;
  flex-grow: 0;
`

const Left = styled(Side)`
  flex: 1;
  justify-content: flex-start;
`

const Right = styled(Side)`
  flex: 1;
  justify-content: flex-end;
`

const Center = styled.div<{ isMobile?: boolean }>`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: proxima nova;
  font-size: ${({ isMobile }) => isMobile ? '20px' : '32px'};
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
`

const Button = styled.div`
  width: 60px;
  height: 49px;
  border-radius: 8px;
  border-width: 2px;
  border-style: solid;
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-color: #333B4F;
  text-align: center;
`

const ActiveButton = styled(Button)`
  border-color: #FF5947;
  background-color: rgba(255, 89, 71, 0.1);
`

const GamesButton = styled(Button)<{ isMobile?: boolean }>`
  ${({ isMobile }) => {
    if (isMobile) return
    return `width: 124px;`
  }}
  height: 49px;
  background-color: #333B4F;
  flex-direction: row;
  font-family: proxima nova;
  font-size: 16px;
  font-weight: 600;
  line-height: 19px;
`

const Underline = styled.div`
  background: #49E69B;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: 2px;
  height: 3px;
  width: 50px;
  margin-top: 3px;
`

const isFullscreen = () => !!fscreen.fullscreenElement

interface HeaderProps {
  collectionName?: string
  screen?: any
}

const Header = ({ collectionName, screen }: HeaderProps) => {
  const navigate = useNavigate()
  const [fullscreen, setFullscreen] = useState<boolean | undefined>()

  useEffect(() => {
    const handleFullscreenChange = (_e: Event) => {
      setFullscreen(isFullscreen())
    }

    fscreen.addEventListener('fullscreenchange', handleFullscreenChange)

    return () => {
      fscreen.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  })

  const redirectToShardLibrary = useCallback(() => {
    navigate({ to: '/shardlibrary' })
  }, [navigate])

  const isMobile = screen?.width < 600

  const isFullscreenAllowed = () => !isApp() && fscreen.fullscreenEnabled

  return (
    <Container>
      <Left isMobile={isMobile}>
        {collectionName !== 'Collections' && (
          <GamesButton isMobile={isMobile} onClick={redirectToShardLibrary}>
            <HomeIcon style={{ width: "14px", height: "15px" }} />
            {!isMobile && (
              <span
                style={{ marginTop: "2px", marginLeft: "12px" }}
              >
                {'Collections'}
              </span>
            )}
          </GamesButton>
        )}

        {collectionName === 'Collections' &&
          <ActiveButton onClick={() => {
            navigate({ to: '/logout' })
          }}>
            <Logout style={{ width: "24px", height: "18px" }} />
          </ActiveButton>
        }
      </Left>
      <Center isMobile={isMobile}>
        <div>{collectionName}</div>
        <Underline />
      </Center>
      <Right isMobile={isMobile}>
        {isFullscreenAllowed() && (
          <Button
            style={{
              marginRight: '10px'
            }}
            onClick={() => {
              if (fullscreen) {
                document.exitFullscreen()
              } else {
                document.documentElement.requestFullscreen()
              }
            }}>
            <FontAwesomeIcon
              style={{
                height: '18px'
              }}
              icon={fullscreen ? faCompress : faExpand}
            />
          </Button>
        )}

        <Button onClick={() => openLink('https://support.gameglass.gg', true)}>
          <QuestionMarkIcon style={{ marginLeft: "-1px", width: "11px", height: "18px" }} />
        </Button>
      </Right>
    </Container>
  )
}

export default withScreenInfo(Header)
